import Immutable from 'seamless-immutable';
import _ from 'lodash';
import RefData from 'ref-data';

import { PROVIDERS } from '../../common/components/Privacy/Providers/Config';

//the clone in _.clone(data) is needed as we need it to return a new clena instance of it
const getMutable = (data, opts = {}) => (Immutable.isImmutable(data) ? data.asMutable(opts) : _.clone(data));
/**
 * Account
 * @typedef {Object} Account
 * @property {number} appVersion - the app version the account is currently set at
 * @property {string[]} enabledFeatures - a list of gated features that have been enabled on the account
 * @property {number} id - the account unique number identifier
 * @property {string} passwordExpirationDays - surprisingly enough it is a string-number and not an actual number, and is used as another layer of securing exported experienice data
 * @property {string} exportPassword - password mostly generated when there has been a request for data to be exported for an experience
 * @property {string} timezone - used to determin the DATE that the account prefers , not to be confused with locale
 * @property {string} locale - used to detemine the LANGUAGE the account prefers, not to be confused with timezone
 * @property {string} country
 * @property {bool} broadcastApiAccess
 * @property {string} name
 * @property {string} intsso - key used mainly for accounts with AppSwitcher
 * @property {string} cessso - key used for the cheetahid user
 * @property {string} emailDomains - key used for the assigned to the account email doamins

  //capabilities specific props that lets determine level of access to an account for specific functionalities
 * @property {bool} accountSettings
 * @property {bool} userManagement
 * @property {bool} analytics
 * @property {bool} consumerSignIn
 * @property {bool} whiteLabelApps
 * @property {bool} labelsAndTranslations
 * @property {bool} consoleExtensions
 * @property {bool} activationPackage
 * @property {bool} advocacyPackage
 * @property {bool} macroExp
 * @property {bool} channelManagement
 * @property {bool} restrictedOptins
 * @property {bool} allowNativeVideo
 * @property {bool} customCode
 * @property {bool} nextGenExp
 * @property {bool} moduleAdvancedSettings
 * @property {bool} moduleDeveloperOptions
 * @property {bool} dataProtection
 * @property {bool} accountIntegration
 * @property {bool} layoutRowSettings
 * @property {bool} allowEmailNotifications
 * @property {bool} emmaDataIntegration
 * @property {bool} campaignMonitorDataIntegration
 * @property {bool} cloudflareTurnstileCaptcha
 * @property {bool} smartGoogleCaptcha
 * @property {bool} alwaysGoogleCaptcha
 * @property {bool} openIdIntegration
 * @property {bool} eventStreamIntegration
 * @property {bool} adobeCommerceProductQuizIntegration
 * @property {bool} showChatKit
 * @property {bool} expressAppSwitcher

 * @property {bool} experienceMetadataEnabled
 * @property {string[]} experienceMetadata
 * @property {bool} hasCampaignExternalRefFormula
 * @property {string} apikey
 *
 * @property {string} scale
 * @property {object} apiData - this is where we store the data we get from the API for the account details, this is to avoid merging it into the main account data as it has some different properties and we want to avoid any unexpected issues with that, so we keep it separate but accessible
 *
 * @property {object} featureFlags - feature flags for the account (key is capability and values is an array of user emails)
 */

const defaultPrivacyConfig = {
  microsite: {
    privacy:  RefData.Enum.STRATEGY.PRIVACYMGMT.NONE,
    provider: PROVIDERS.ONE_TRUST,
  },
  webEmbedLightbox: {
    privacy:  RefData.Enum.STRATEGY.PRIVACYMGMT.NONE,
    provider: PROVIDERS.ONE_TRUST,
  },
};

/**
 * Create an AccountModel from provided data
 * @param {Object} account - the account as a POJO
 */
function create(account = _defaultAccount) {
  let result = _.clone(account);

  result.enabledFeatures = result.enabledFeatures ? _.split(result.enabledFeatures, ',') : [];

  result.passwordExpirationDays = result.passwordExpirationDays ? result.passwordExpirationDays.toString() : '';
  result.exportPassword = result.exportPassword || '';

  result._capabilities = _.pick(result, _capabilityKeys); //pick capability related props into _capabilities
  result = _.omit(result, _capabilityKeys); //remove capability related props from result

  result.experienceMetadata = _.isString(result.experienceMetadata)
    ? JSON.parse(result.experienceMetadata)
    : result.experienceMetadata;

  result.privacyConfig = _.merge({}, defaultPrivacyConfig, result.privacyConfig);

  result.featureFlags = result.featureFlags || {};

  delete result.capabilities;

  return Immutable(result, { prototype: model });
}

/**
 * Transform a backend accouunt object into a frontend AccountModel
 * @param {Object} account - backend formatted accountt
 * @returns {Account}
 */
function deserialize(account) {
  //we have not fully mapped all properties that we need from the account global context to the new account react based model
  //when we get data from the BE via the API we are missing some data (for example capabilities settings) that we currently only have access via App.globalContext - hence once we get data from the BE we map it over our existing globalContext instance to fill up that gap
  const _account = _.extend({}, App.globalContext.account, account);
  const result = _.pick(_account, _keys);

  result.socialAuthConfig = _.isString(result.socialAuthConfig)
    ? JSON.parse(result.socialAuthConfig)
    : result.socialAuthConfig;

  //this code deals with some of the issues we get from _consolePreScripts account props having a different type as the one we get from the api
  result.intsso = result.intsso || '';
  result.cessso = result.cessso || '';

  result.timezone = _.get(result, 'timezone.id', result.timezone);
  result.country = _.get(result, 'country.id', result.country);
  result.privacyConfig = _.isString(result.privacyConfig) ? JSON.parse(result.privacyConfig) : result.privacyConfig;

  result.storageRetentionPeriod = result.storageRetentionPeriod || 12;

  return create(result);
}

/**
 * Transform an AccountModel into a backend account object
 * @param {Account} account
 * @returns {Object}
 */
function serialize(account) {
  const result = getMutable(account, { deep: true });
  result.enabledFeatures = _.isEmpty(account.enabledFeatures) ? null : _.join(account.enabledFeatures, ',');

  //<Input> as an element returns a string and not a number
  result.passwordExpirationDays = parseInt(result.passwordExpirationDays, 10) || '';

  result.privacyConfig = JSON.stringify(result.privacyConfig);

  result.socialAuthConfig = JSON.stringify(result.socialAuthConfig);

  result.plan = _.isString(result.plan) ? result.plan : result.plan.id;

  //only for internal use
  delete result.capabilities;
  delete result.experienceMetadataEnabled;

  //should not be sent
  delete result.name;
  delete result.intsso;
  delete result.privateApikey;
  delete result.apikey;
  delete result.apiData;

  //should not be sent, this puts quite a lot of effort on the BE to validate, currently can only be set in the M area
  //but the content is used in the console area
  delete result.experienceMetadata;

  return _.pick(result, _keys);
}

const model = {
  get isIntSso() {
    return !!this.intsso;
  },

  get capabilities() {
    return this._capabilities;
  },
  set capabilities(value) {
    //[Victor] might have to make this one a bit more complex in the future to make sure we set it in the correct shape
    //this is transient value for the FE
    this._capabilities = value;
    return this;
  },

  get isExperienceMetadataEnabled() {
    return this.experienceMetadataEnabled;
  },

  get hasBroadcastApiAccess() {
    return this.broadcastApiAccess;
  },
};

const _capabilityKeys = [
  'consoleExtensions',
  'activationPackage',
  'advocacyPackage',
  'restrictedOptins',
  'allowNativeVideo',
  'macroExp',
  'customCode',
  'accountSettings',
  'userManagement',
  'analytics',
  'consumerSignIn',
  'whiteLabelApps',
  'labelsAndTranslations',
  'nextGenExp',
  'searchExperiencesAcrossSubaccounts',
  'channelManagement',
  'moduleAdvancedSettings',
  'moduleDeveloperOptions',
  'dataProtection',
  'accountIntegration',
  'layoutRowSettings',
  'allowEmailNotifications',
  'emmaDataIntegration',
  'campaignMonitorDataIntegration',
  'cloudflareTurnstileCaptcha',
  'smartGoogleCaptcha',
  'alwaysGoogleCaptcha',
  'openIdIntegration',
  'eventStreamIntegration',
  'adobeCommerceProductQuizIntegration',
  'showChatKit',
  'nativeVideo',
  'zmpOnLoadIntegration',
  'nextgenRememberMe',
  'expressAppSwitcher',
];

const _keys = [
  'appVersion',
  'enabledFeatures',
  'id',
  'passwordExpirationDays',
  'exportPassword',
  'timezone',
  'locale',
  'country',
  'broadcastApiAccess',
  'name',
  'intsso',
  'cessso',

  'experienceMetadataEnabled',
  'experienceMetadata',
  'hasCampaignExternalRefFormula',

  'privacyConfig',
  'apikey',
  'scale',
  'plan',

  'emailDomains',
  'storageRetentionPeriod',

  'integrationBlueprint',
  'socialAuthConfig',

  'providerProperties',

  'hasSubaccounts',

  'apiData',

  'featureFlags',

  //capabilities specific props
  ..._capabilityKeys,
];

const _defaultAccount = {};

export default {
  create,
  deserialize,
  serialize,
};
